/**
 * Budget write tools — create, update and delete budget items by reusing the
 * existing Firestore helpers in features/budget/operations.ts.
 */

import { Schema } from "firebase/ai";
import { BudgetItemSchema, type BudgetItem } from "@amazing-wedding/shared-types";
import type { PlannerTool } from "./registry";
import { asBoolean, asNumber, asEnum, asString, normalizeDate, requireString } from "./helpers";

const CATEGORY_VALUES = [
  "venue",
  "catering",
  "photography",
  "music",
  "flowers",
  "attire",
  "transport",
  "beauty",
  "invitations",
  "rings",
  "cake",
  "other",
] as const;

type NewBudgetItem = Omit<BudgetItem, "id" | "createdAt" | "updatedAt">;
type BudgetItemPatch = Partial<NewBudgetItem>;

const NewBudgetItemSchema = BudgetItemSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

function itemLabel(
  ctx: { budgetItems: () => { id: string; category: string; supplierName?: string | null }[] },
  id: string,
): string {
  const item = ctx.budgetItems().find((b) => b.id === id);
  if (!item) return id;
  return item.supplierName ? `${item.category} — ${item.supplierName}` : item.category;
}

export const budgetTools: PlannerTool[] = [
  {
    declaration: {
      name: "create_budget_item",
      description:
        "Add a new budget item / expense to the wedding budget. Use when the user wants to track a cost or supplier.",
      parameters: Schema.object({
        properties: {
          category: Schema.enumString({
            enum: [...CATEGORY_VALUES],
            description: "Budget category. Pick the closest match; use 'other' if unsure.",
          }),
          estimatedCost: Schema.number({ description: "Estimated cost, in the wedding currency." }),
          supplierName: Schema.string({ description: "Supplier / vendor name (optional)." }),
          actualCost: Schema.number({
            description: "Actual cost so far (optional, defaults to 0).",
          }),
          depositPaid: Schema.number({
            description: "Deposit already paid (optional, defaults to 0).",
          }),
          dueDate: Schema.string({ description: "Payment due date in ISO-8601 (optional)." }),
          isPaid: Schema.boolean({ description: "Whether this item is fully paid (optional)." }),
          notes: Schema.string({ description: "Free-text notes (optional)." }),
        },
        optionalProperties: [
          "supplierName",
          "actualCost",
          "depositPaid",
          "dueDate",
          "isPaid",
          "notes",
        ],
      }),
    },
    destructive: false,
    describe: (args) => {
      const supplier = asString(args.supplierName);
      const category = asString(args.category) ?? "?";
      return `Dodanie wydatku: ${supplier ? `${category} — ${supplier}` : category}`;
    },
    execute: async (args, ctx) => {
      const category = asEnum(args.category, CATEGORY_VALUES);
      if (!category) throw new Error("A valid 'category' is required.");
      const input: NewBudgetItem = {
        category,
        supplierName: asString(args.supplierName) ?? null,
        estimatedCost: asNumber(args.estimatedCost) ?? 0,
        actualCost: asNumber(args.actualCost) ?? 0,
        depositPaid: asNumber(args.depositPaid) ?? 0,
        dueDate: normalizeDate(args.dueDate) ?? null,
        isPaid: asBoolean(args.isPaid) ?? false,
        notes: asString(args.notes) ?? null,
      };
      const parsed = NewBudgetItemSchema.parse(input);
      const id = await ctx.operations.budget.addBudgetItem(ctx.weddingId, parsed);
      return { ok: true, budgetItemId: id, category, estimatedCost: input.estimatedCost };
    },
  },
  {
    declaration: {
      name: "update_budget_item",
      description:
        "Update an existing budget item. You MUST first call list_budget to obtain the exact budgetItemId. Only the fields you pass are changed.",
      parameters: Schema.object({
        properties: {
          budgetItemId: Schema.string({ description: "Exact budget item ID from list_budget." }),
          category: Schema.enumString({ enum: [...CATEGORY_VALUES] }),
          supplierName: Schema.string(),
          estimatedCost: Schema.number(),
          actualCost: Schema.number(),
          depositPaid: Schema.number(),
          dueDate: Schema.string({ description: "Payment due date in ISO-8601." }),
          isPaid: Schema.boolean(),
          notes: Schema.string(),
        },
        optionalProperties: [
          "category",
          "supplierName",
          "estimatedCost",
          "actualCost",
          "depositPaid",
          "dueDate",
          "isPaid",
          "notes",
        ],
      }),
    },
    destructive: false,
    describe: (args, ctx) =>
      `Aktualizacja wydatku: ${itemLabel(ctx, asString(args.budgetItemId) ?? "")}`,
    execute: async (args, ctx) => {
      const id = requireString(args, "budgetItemId");
      if (!ctx.budgetItems().some((b) => b.id === id)) {
        throw new Error(`No budget item with ID "${id}". Call list_budget first.`);
      }
      const patch: BudgetItemPatch = {};
      const category = asEnum(args.category, CATEGORY_VALUES);
      const supplierName = asString(args.supplierName);
      const estimatedCost = asNumber(args.estimatedCost);
      const actualCost = asNumber(args.actualCost);
      const depositPaid = asNumber(args.depositPaid);
      const dueDate = normalizeDate(args.dueDate);
      const isPaid = asBoolean(args.isPaid);
      const notes = asString(args.notes);
      if (category !== undefined) patch.category = category;
      if (supplierName !== undefined) patch.supplierName = supplierName;
      if (estimatedCost !== undefined) patch.estimatedCost = estimatedCost;
      if (actualCost !== undefined) patch.actualCost = actualCost;
      if (depositPaid !== undefined) patch.depositPaid = depositPaid;
      if (dueDate !== undefined) patch.dueDate = dueDate;
      if (isPaid !== undefined) patch.isPaid = isPaid;
      if (notes !== undefined) patch.notes = notes;
      if (Object.keys(patch).length === 0) {
        throw new Error("No fields to update were provided.");
      }
      await ctx.operations.budget.updateBudgetItem(ctx.weddingId, id, patch);
      return { ok: true, budgetItemId: id, updated: Object.keys(patch) };
    },
  },
  {
    declaration: {
      name: "delete_budget_item",
      description:
        "Permanently delete a budget item. You MUST first call list_budget to obtain the exact budgetItemId.",
      parameters: Schema.object({
        properties: {
          budgetItemId: Schema.string({ description: "Exact budget item ID from list_budget." }),
        },
      }),
    },
    destructive: true,
    describe: (args, ctx) =>
      `Usunięcie wydatku: ${itemLabel(ctx, asString(args.budgetItemId) ?? "")}`,
    execute: async (args, ctx) => {
      const id = requireString(args, "budgetItemId");
      if (!ctx.budgetItems().some((b) => b.id === id)) {
        throw new Error(`No budget item with ID "${id}". Call list_budget first.`);
      }
      await ctx.operations.budget.deleteBudgetItem(ctx.weddingId, id);
      return { ok: true, budgetItemId: id };
    },
  },
];
